import i18n from '@src/i18n'
import { CurrencyComputed } from '@state/modules/currency'
import { OrganizationComputed } from '@state/modules/organization'

export default {
  computed: {
    ...CurrencyComputed,
    ...OrganizationComputed,
  },
  methods: {
    /**
     * Formats a number as currency or plain number, using the current app locale and a formatting switch.
     *
     * Formatting Logic:
     * - The formatting switch is controlled by `organization.enabledCurrencyFormat`.
     *
     * - If the switch is ON (`enabledCurrencyFormat === true`):
     *     - Always formats the amount with the currency symbol (e.g., ₹1,234.56), regardless of `mode`.
     *     - The currency code (e.g., "INR") is never shown in this case.
     *
     * - If the switch is OFF (`enabledCurrencyFormat === false`):
     *     - If `mode` is 'code':
     *         - Shows the plain number and the currency code (e.g., 1234.56 INR).
     *     - If `mode` is 'symbol' (or omitted):
     *         - Shows the plain number only (e.g., 1234.56).
     *
     * - The function uses the organization's default language for locale-aware formatting.
     * - Handles missing or invalid input gracefully.
     *
     * @param {number} amount - The numeric amount to format.
     * @param {number|string} currencyId - The currency identifier (id, key, name, or code).
     * @param {string} [mode='symbol'] - Display mode: 'symbol' (default) or 'code'.
     *   - 'symbol':
     *       - Switch ON: formatted with currency symbol (e.g., ₹1,234.56)
     *       - Switch OFF: plain number only (e.g., 1234.56)
     *   - 'code':
     *       - Switch ON: formatted with currency symbol (e.g., ₹1,234.56)
     *       - Switch OFF: plain number and code (e.g., 1234.56 INR)
     * @returns {string} - The formatted currency string.
     */
    formatCurrency(amount, currencyId, mode = 'symbol') {
      const { enabledCurrencyFormat } = this.organization

      const locale = i18n.locale || 'en'
      let currencyObj = null
      let currencyCode = null
      let codeForDisplay = ''
      let nfOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 }

      const formatEnabled = enabledCurrencyFormat

      if (
        currencyId &&
        this.currencyOptions &&
        Array.isArray(this.currencyOptions)
      ) {
        currencyObj = this.currencyOptions.find(
          (c) =>
            c.id === currencyId || c.key === currencyId || c.name === currencyId
        )
      }
      if (currencyObj) {
        currencyCode = currencyObj.code || currencyObj.name
      } else if (typeof currencyId === 'string') {
        currencyCode = currencyId
      }
      codeForDisplay = currencyCode || ''

      // When formatEnabled is true, always format and show the symbol (never the code)
      if (formatEnabled) {
        if (currencyCode) {
          nfOptions.style = 'currency'
          nfOptions.currency = currencyCode
          nfOptions.currencyDisplay = 'symbol'
        }
        try {
          return new Intl.NumberFormat(locale, nfOptions).format(amount)
        } catch (e) {
          return amount != null ? parseFloat(amount).toFixed(2) : ''
        }
      }

      // When switch is OFF
      if (!formatEnabled) {
        if (mode === 'code') {
          // Show plain number and code
          return codeForDisplay
            ? `${
                amount != null ? parseFloat(amount).toFixed(2) : ''
              } ${codeForDisplay}`
            : amount != null
            ? parseFloat(amount).toFixed(2)
            : ''
        } else {
          // For 'symbol' (or default), show plain number only
          return amount != null ? parseFloat(amount).toFixed(2) : ''
        }
      }

      // fallback
      return amount != null ? parseFloat(amount).toFixed(2) : ''
    },
  },
}
